import React, { useState, useEffect, useRef, useCallback } from "react";
import { Avatar, IconButton, Card, CardContent, CardHeader, CardMedia, Typography, TextField, Button, CircularProgress, Box, Grid, Badge, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemAvatar, ListItemText, Switch, FormControlLabel, InputAdornment, Menu, MenuItem, Chip, Divider, Paper, Tabs, Tab, DialogContentText } from "@mui/material";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "./translations/useTranslation";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ShareIcon from "@mui/icons-material/Share";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import ReplyIcon from "@mui/icons-material/Reply";
import SearchIcon from "@mui/icons-material/Search";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import PolicyIcon from "@mui/icons-material/Policy";
import PostAddIcon from "@mui/icons-material/PostAdd";
import ArticleIcon from "@mui/icons-material/Article";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks"; // Add LibraryBooksIcon import
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { formatDistanceToNow } from 'date-fns';
import GovernmentSchemes from "./components/GovernmentSchemes/GovernmentSchemes";
import AgricultureNews from "./components/GovernmentSchemes/IndianAgricultureNews";
import Articles from "./components/Articles/Articles"; // Add Articles import

const getRelativeTime = (date) => formatDistanceToNow(new Date(date), { addSuffix: true });

// Removed CLOUDINARY_URL as we're now using server-side uploads

// Debounce function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const SocialMedia = () => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState("");
  const [newComment, setNewComment] = useState("");
  const fileInputRef = useRef();
  const userId = localStorage.getItem("userId");
  const [showAllPosts, setShowAllPosts] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  // New states for friend requests and sharing
  const [shareMenuAnchor, setShareMenuAnchor] = useState(null);
  const [sharePost, setSharePost] = useState(null);
  const [followingStatus, setFollowingStatus] = useState({});
  const [followLoading, setFollowLoading] = useState({});
  const [replyToComment, setReplyToComment] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [postComments, setPostComments] = useState({});
  const [showAllComments, setShowAllComments] = useState({});
  const [shareMessage, setShareMessage] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  
  // New states for image modal and description expansion
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [expandedPosts, setExpandedPosts] = useState({});
  
  // New state for post menu
  const [postMenuAnchor, setPostMenuAnchor] = useState(null);
  const [selectedPostId, setSelectedPostId] = useState(null);

  useEffect(() => { fetchPosts(); }, [showAllPosts]);

  // Fetch following status for all posts
  const fetchFollowingStatus = async (postsData) => {
    if (!userId) return;
    const statusMap = {};
    for (const post of postsData) {
      if (post.user._id === userId) {
        statusMap[post.user._id] = false;
        continue;
      }
      try {
        const res = await fetch(`https://api.agromanch.com/api/friends/list/${userId}`);
        const friends = await res.json();
        statusMap[post.user._id] = friends.some(f => f._id === post.user._id);
      } catch {
        statusMap[post.user._id] = false;
      }
    }
    setFollowingStatus(statusMap);
  };

  const fetchPosts = async () => {
    // Use AbortController to cancel previous requests
    const controller = new AbortController();
    
    // Cancel previous request if exists
    if (window.postsController) {
      window.postsController.abort();
    }
    
    // Store current request controller
    window.postsController = controller;
    
    let url = showAllPosts
      ? "https://api.agromanch.com/api/posts?all=true"
      : `https://api.agromanch.com/api/posts?userId=${userId}`;
    try {
      console.log("=== FETCHING POSTS ===");
      console.log("Fetch URL:", url);
      console.log("Show all posts:", showAllPosts);
      
      const res = await fetch(url, { signal: controller.signal });
      console.log("Posts response status:", res.status);
      
      // Clean up controller
      delete window.postsController;
      
      if (!res.ok) {
        console.error("Failed to fetch posts:", res.status, res.statusText);
        setMessage("Failed to load feed.");
        return;
      }
      
      const data = await res.json();
      console.log("Posts data received:", data);
      console.log("Number of posts:", data.length);
      
      // Log image data for each post
      data.forEach((post, index) => {
        console.log(`Post ${index + 1}:`, {
          id: post._id,
          content: post.content,
          image: post.image,
          video: post.video,
          user: post.user?.name
        });
      });
      
      setPosts(data);
      fetchFollowingStatus(data);
    } catch (err) {
      // Ignore abort errors
      if (err.name !== 'AbortError') {
        console.error("Fetch posts error:", err);
        setMessage("Failed to load feed.");
      }
    }
  };

  // Like/unlike post - Optimized version
  const handleLike = async (post) => {
    if (!userId) return;
    try {
      // Optimistically update UI
      // Handle case where likes might be undefined
      const currentLikes = post.likes && Array.isArray(post.likes) ? post.likes : [];
      const isLiked = currentLikes.includes(userId);
      const updatedLikes = isLiked 
        ? currentLikes.filter(id => id !== userId)
        : [...currentLikes, userId];
      
      // Update UI immediately for better UX
      setPosts(prevPosts => 
        prevPosts.map(p => 
          p._id === post._id 
            ? { ...p, likes: updatedLikes }
            : p
        )
      );
      
      // Make API call
      const res = await fetch(`https://api.agromanch.com/api/posts/${post._id}/like`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });
      
      if (res.ok) {
        // Update with actual server response
        const updatedPost = await res.json();
        setPosts(prevPosts => 
          prevPosts.map(p => 
            p._id === post._id 
              ? { ...p, likes: updatedPost.likes }
              : p
          )
        );
      } else {
        // Revert UI update if API call fails
        setPosts(prevPosts => 
          prevPosts.map(p => 
            p._id === post._id 
              ? { ...p, likes: post.likes || [] }
              : p
          )
        );
      }
    } catch (err) {
      // Revert UI update if API call fails
      setPosts(prevPosts => 
        prevPosts.map(p => 
          p._id === post._id 
            ? { ...p, likes: post.likes || [] }
            : p
        )
      );
    }
  };

  // Handle follow/unfollow
  const handleFollowToggle = async (targetUserId) => {
    if (!userId) return;
    const isFollowing = followingStatus[targetUserId];
    const endpoint = isFollowing
      ? `https://api.agromanch.com/api/friends/unfollow`
: `https://api.agromanch.com/api/friends/request`;
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fromUserId: userId, toUserId: targetUserId }),
      });
      if (res.ok) {
        setFollowingStatus(prev => ({ ...prev, [targetUserId]: !isFollowing }));
      } else {
        const data = await res.json();
        console.error("Follow/unfollow error:", data.error);
        setMessage(data.error || "Failed to update follow status.");
        setTimeout(() => setMessage(""), 3000);
      }
    } catch (err) {
      console.error("Follow/unfollow error:", err);
      setMessage("Error updating follow status.");
      setTimeout(() => setMessage(""), 3000);
    } finally {
      // Clear loading state after request completes
      setFollowLoading(prev => ({ ...prev, [targetUserId]: false }));
    }
  };

  // Share post with options
  const handleShareClick = (event, post) => {
    setShareMenuAnchor(event.currentTarget);
    setSharePost(post);
  };

  const handleShareClose = () => {
    setShareMenuAnchor(null);
    setSharePost(null);
  };

  const handleShareOption = (option) => {
    if (!sharePost) return;
    const postUrl = `${window.location.origin}/post/${sharePost._id}`;
    const postText = `${sharePost.content}\n\nView post: ${postUrl}`;
    switch (option) {
      case 'email':
        window.open(`mailto:?subject=Check out this post&body=${encodeURIComponent(postText)}`);
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(postText)}`);
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postUrl)}`);
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(postText)}`);
        break;
      case 'copy':
        navigator.clipboard.writeText(postText);
        setShareMessage("Link copied to clipboard!");
        break;
      default:
        break;
    }
    handleShareClose();
    setTimeout(() => setShareMessage(""), 2000);
  };

  // Load comments for a specific post - Optimized version
  const loadComments = async (postId) => {
    try {
      // Use AbortController to cancel previous requests for the same post
      const controller = new AbortController();
      
      // Check if we already have a pending request for this post
      if (window.commentRequests && window.commentRequests[postId]) {
        window.commentRequests[postId].abort();
      }
      
      // Store the current request
      if (!window.commentRequests) window.commentRequests = {};
      window.commentRequests[postId] = controller;
      
      const res = await fetch(`https://api.agromanch.com/api/posts/${postId}/comments`, {
        signal: controller.signal
      });
      
      // Clean up
      delete window.commentRequests[postId];
      
      if (res.ok) {
        const data = await res.json();
        setPostComments(prev => ({ ...prev, [postId]: data }));
      } else {
        setPostComments(prev => ({ ...prev, [postId]: [] }));
      }
    } catch (err) {
      // Ignore abort errors
      if (err.name !== 'AbortError') {
        setPostComments(prev => ({ ...prev, [postId]: [] }));
      }
    }
  };

  // Toggle show all comments for a post
  const toggleShowAllComments = (postId) => {
    setShowAllComments(prev => ({ ...prev, [postId]: !prev[postId] }));
    if (!postComments[postId]) {
      loadComments(postId);
    }
  };

  // Add comment to a post - Optimized version with debouncing
  const handleAddComment = useCallback(async (postId, commentText, parentCommentId = null) => {
    if (!userId || !commentText.trim()) return false;
    try {
      // Create temporary comment for immediate UI update
      const tempComment = {
        _id: `temp-${Date.now()}`,
        user: {
          _id: userId,
          name: localStorage.getItem("authname") || "You",
          picture: localStorage.getItem("authpicture") || null
        },
        text: commentText,
        createdAt: new Date().toISOString(),
        parentComment: parentCommentId,
        replies: []
      };
      
      // Optimistically update UI
      setPostComments(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), tempComment]
      }));
      
      // Make API call
      const res = await fetch(`https://api.agromanch.com/api/posts/${postId}/comment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          userId, 
          text: commentText,
          parentCommentId
        }),
      });
      
      if (res.ok) {
        // Replace temporary comment with actual comment from server
        const actualComment = await res.json();
        setPostComments(prev => ({
          ...prev,
          [postId]: prev[postId].map(comment => 
            comment._id === tempComment._id ? actualComment : comment
          )
        }));
        return true;
      } else {
        // Remove temporary comment if API call fails
        setPostComments(prev => ({
          ...prev,
          [postId]: prev[postId].filter(comment => comment._id !== tempComment._id)
        }));
        return false;
      }
    } catch (err) {
      // Remove temporary comment if API call fails
      setPostComments(prev => ({
        ...prev,
        [postId]: prev[postId].filter(comment => comment._id !== `temp-${Date.now()}`)
      }));
      return false;
    }
  }, [userId]);

  // Handle reply to comment
  const handleReplyClick = (comment) => {
    setReplyToComment(comment);
    setReplyText("");
  };

  const handleCancelReply = () => {
    setReplyToComment(null);
    setReplyText("");
  };

  // UI for post creation
  const renderPostForm = () => (
    <Card 
      sx={{ 
        mb: 4, 
        p: 2, 
        borderRadius: 3, 
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        bgcolor: 'background.paper',
        overflow: 'visible'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Avatar 
          src={localStorage.getItem("authpicture")} 
          sx={{ 
            width: 48, 
            height: 48, 
            mr: 2,
            border: '2px solid #e0e0e0'
          }}
        />
        <Typography fontWeight={600}>
          {localStorage.getItem("authname") || "ABC"}
        </Typography>
      </Box>
      <form onSubmit={handleSubmit}>
        <TextField
          multiline
          minRows={2}
          maxRows={3}
          fullWidth
          variant="outlined"
          placeholder="What's on your mind?"
          value={content}
          onChange={e => setContent(e.target.value)}
          sx={{ 
            mb: 2, 
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: '#f8f9fa',
              '& fieldset': {
                borderColor: '#e0e0e0',
              },
              '&:hover fieldset': {
                borderColor: '#bdbdbd',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              }
            }
          }}
          InputProps={{
            sx: { px: 2, py: 1.5 }
          }}
        />
        {mediaPreview && (
          <Box sx={{ mb: 2, position: 'relative' }}>
            {mediaType === "image" ? (
              <img src={mediaPreview} alt="preview" style={{ maxWidth: '100%', borderRadius: 8, maxHeight: 400 }} />
            ) : (
              <Box sx={{ position: 'relative' }}>
                <video src={mediaPreview} controls style={{ maxWidth: '100%', borderRadius: 8, maxHeight: 400 }} />
                <Chip 
                  label="Video" 
                  color="primary" 
                  size="small" 
                  sx={{ 
                    position: 'absolute', 
                    top: 8, 
                    right: 8,
                    fontWeight: 'bold'
                  }} 
                />
              </Box>
            )}
            {uploading && (
              <Box sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                bgcolor: 'rgba(0,0,0,0.5)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                borderRadius: 8
              }}>
                <CircularProgress 
                  variant="determinate" 
                  value={uploadProgress} 
                  size={60} 
                  thickness={4}
                  sx={{ color: 'white' }}
                />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    position: 'absolute', 
                    color: 'white', 
                    fontWeight: 'bold' 
                  }}
                >
                  {Math.round(uploadProgress)}%
                </Typography>
              </Box>
            )}
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
          <Box>
            <input
              type="file"
              accept="image/*,video/*"
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={handleFileChange}
            />
            <IconButton 
              color="primary" 
              onClick={() => fileInputRef.current.click()}
              sx={{ 
                bgcolor: 'rgba(25, 118, 210, 0.08)', 
                mr: 1,
                '&:hover': {
                  bgcolor: 'rgba(25, 118, 210, 0.15)',
                }
              }}
            >
              <AddPhotoAlternateIcon />
            </IconButton>
            <IconButton 
              color="secondary" 
              onClick={() => fileInputRef.current.click()}
              sx={{ 
                bgcolor: 'rgba(156, 39, 176, 0.08)', 
                '&:hover': {
                  bgcolor: 'rgba(156, 39, 176, 0.15)',
                }
              }}
            >
              <VideoLibraryIcon />
            </IconButton>
            {uploading && <CircularProgress size={24} sx={{ ml: 2 }} />}
          </Box>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={uploading || (!content.trim() && !mediaPreview)}
            sx={{ 
              px: 4, 
              py: 1, 
              background: 'linear-gradient(to right, var(--chakra-colors-green-400), var(--chakra-colors-teal-500))',
              fontWeight: 'bold',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(to right, var(--chakra-colors-green-500), var(--chakra-colors-teal-600))',
              },
              '&.Mui-disabled': {
                background: '#e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            POST
          </Button>
        </Box>
      </form>
      
      {/* Error/Success Dialog */}
      <Dialog
        open={!!message}
        onClose={() => setMessage("")}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {message === "Post created!" ? "Success" : "Error"}
        </DialogTitle>
        <DialogContent>
          <Typography>{message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessage("")} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );

  // Render comments section for a post - Memoized version
  const renderComments = useCallback((post) => {
    const comments = postComments[post._id] || [];
    const showAll = showAllComments[post._id];
    const displayComments = showAll ? comments : comments.slice(0, 2);
    const hasMoreComments = comments.length > 2;
    return (
      <Box sx={{ mt: 1 }}>
        {/* Comments display */}
        {displayComments.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {displayComments.map((comment) => (
              <Box
                key={comment._id}
                sx={{
                  mb: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid #f0f0f0",
                  backgroundColor: "#f8f9fa",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                  textAlign: 'left'
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Avatar 
                    src={comment.user?.picture} 
                    sx={{ width: 28, height: 28 }} 
                  />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      color: '#333' 
                    }}
                  >
                    {comment.user?.name || "User"}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: "#6c757d", 
                      marginLeft: "auto",
                      fontSize: '0.75rem' 
                    }}
                  >
                    {new Date(comment.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: "#495057", 
                    ml: 4, 
                    mb: 0.5, 
                    textAlign: 'left',
                    fontSize: '0.85rem'
                  }}
                >
                  {comment.text}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                  <Button
                    size="small"
                    sx={{ 
                      minWidth: "auto", 
                      p: 0, 
                      color: "#6c757d",
                      fontSize: '0.75rem',
                      textTransform: 'none'
                    }}
                    onClick={() => handleReplyClick(comment)}
                  >
                    Reply
                  </Button>
                </Box>
                {/* Reply input */}
                {replyToComment && replyToComment._id === comment._id && (
                  <Box sx={{ ml: 4, mt: 1, textAlign: 'left' }}>
                    <TextField
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder={`Reply to ${comment.user?.name}...`}
                      fullWidth
                      size="small"
                      variant="outlined"
                      sx={{ 
                        mb: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          fontSize: '0.85rem',
                          backgroundColor: '#fff',
                        }
                      }}
                    />
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={async () => {
                          const success = await handleAddComment(post._id, replyText, comment._id);
                          if (success) {
                            setReplyText("");
                            setReplyToComment(null);
                          }
                        }}
                        disabled={!replyText.trim()}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          borderRadius: 15,
                          px: 2
                        }}
                      >
                        Reply
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={handleCancelReply}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          borderRadius: 15,
                          px: 2
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                )}
                {/* Replies */}
                {comment.replies && comment.replies.length > 0 && (
                  <Box sx={{ ml: 4, mt: 1, textAlign: 'left' }}>
                    {comment.replies.map((reply) => (
                      <Box
                        key={reply._id}
                        sx={{
                          mb: 1,
                          p: 1.2,
                          border: "1px solid #f0f0f0",
                          borderRadius: 2,
                          backgroundColor: "#f9fafb",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Avatar src={reply.user?.picture} sx={{ width: 24, height: 24 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                            {reply.user?.name || "User"}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#6c757d", marginLeft: "auto", fontSize: '0.7rem' }}>
                            {new Date(reply.createdAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: "#495057", ml: 4, mt: 0.5, fontSize: '0.8rem' }}>
                          {reply.text}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
        {/* Show more comments button */}
        {hasMoreComments && !showAll && (
          <Button
            size="small"
            onClick={() => toggleShowAllComments(post._id)}
            sx={{ 
              color: "#1976d2", 
              mb: 1, 
              fontSize: '0.8rem',
              textTransform: 'none'
            }}
          >
            View all {comments.length} comments
          </Button>
        )}
        {/* Add comment input */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            fullWidth
            size="small"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 20,
                backgroundColor: "#f8f9fa",
                fontSize: '0.85rem',
                px: 1,
              },
            }}
          />
          <Button
            onClick={async () => {
              const success = await handleAddComment(post._id, newComment);
              if (success) {
                setNewComment("");
              }
            }}
            disabled={!newComment.trim()}
            variant="contained"
            size="small"
            sx={{
              borderRadius: 20,
              textTransform: 'none',
              px: 2,
              fontSize: '0.8rem',
              minWidth: 60,
              color: 'white',
              background: 'linear-gradient(to right, var(--chakra-colors-green-400), var(--chakra-colors-teal-500))',
              '&:hover': {
                background: 'linear-gradient(to right, var(--chakra-colors-green-500), var(--chakra-colors-teal-600))',
              },
              '&.Mui-disabled': {
                background: '#e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            Post
          </Button>
        </Box>
      </Box>
    );
  }, [postComments, showAllComments, replyToComment, replyText, newComment, handleAddComment]);

  // UI for feed
  const renderFeed = () => (
    <Box sx={{ width: '100%' }}>
      {posts.map(post => {
        // Handle case where likes might be undefined
        const currentLikes = post.likes && Array.isArray(post.likes) ? post.likes : [];
        const liked = currentLikes.includes(userId);
        const isFollowing = followingStatus[post.user._id];
        const isOwnPost = post.user._id === userId;
        return (
          <Card 
            key={post._id} 
            sx={{ 
              mb: 3, 
              borderRadius: 3, 
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              overflow: 'visible',
              '&:hover': { 
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)' 
              }
            }}
          >
            <Box sx={{ p: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                mb: 2 
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 <Link to={`/profile/${post.user?.email}`} style={{ textDecoration: "none" }}>
                <Avatar 
                    src={post.user?.picture} 
                    sx={{ 
                      width: 40, 
                      height: 40, 
                      mr: 1.5,
                      border: '1px solid #e0e0e0' 
                    }}
                  />
                </Link>
                  <Box>
                    <Typography 
                      sx={{ 
                        fontSize: 15, 
                        fontWeight: 600, 
                        color: '#333',
                        lineHeight: 1.2
                      }}
                    >
                      <Link to={`/profile/${post.user?.email}`} style={{ textDecoration: "none", color: "inherit" }}>
                        {post.user?.name || "User"}
                      </Link>
                    </Typography>
                    <Typography 
                      sx={{ 
                        fontSize: 12, 
                        color: 'text.secondary',
                        lineHeight: 1.2
                      }}
                    >
                      {getRelativeTime(post.createdAt)}
                    </Typography>
                  </Box>
                </Box>
                {!isOwnPost && (
                  <Button
                    size="small"
                    startIcon={isFollowing ? <PersonRemoveIcon /> : <PersonAddIcon />}
                    variant={isFollowing ? "contained" : "outlined"}
                    color={isFollowing ? "primary" : "primary"}
                    sx={{ 
                      borderRadius: 20,
                      px: 2,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      boxShadow: isFollowing ? 1 : 0,
                      minWidth: '100px'
                    }}
                    onClick={() => handleFollowToggle(post.user._id)}
                    disabled={followLoading[post.user._id]}
                  >
                    {followLoading[post.user._id] ? (
                      <>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        {isFollowing ? "Unfollowing..." : "Following..."}
                      </>
                    ) : (
                      isFollowing ? "Unfollow" : "Follow"
                    )}
                  </Button>
                )}
                {isOwnPost && (
                  <>
                    <IconButton
                      onClick={(event) => handlePostMenuOpen(event, post._id)}
                      sx={{ 
                        color: 'text.secondary',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      anchorEl={postMenuAnchor}
                      open={Boolean(postMenuAnchor) && selectedPostId === post._id}
                      onClose={handlePostMenuClose}
                      PaperProps={{
                        sx: {
                          borderRadius: 2,
                          boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                        }
                      }}
                    >
                      <MenuItem onClick={handleDeletePostFromMenu} sx={{ color: 'error.main' }}>
                        <DeleteIcon sx={{ mr: 1 }} />
                        Delete
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Box>

              {renderDescription(post)}

              {post.image && (
                <Box 
                  sx={{ 
                    mb: 2, 
                    borderRadius: 2, 
                    overflow: 'hidden',
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                  onClick={() => openImageModal(post.image)}
                >
                  <CardMedia 
                    component="img" 
                    image={post.image} 
                    alt="post" 
                    sx={{ 
                      maxHeight: 350, 
                      objectFit: "cover",
                      width: '100%'
                    }} 
                  />
                  <Box sx={{ 
                    position: 'absolute', 
                    bottom: 8, 
                    right: 8, 
                    bgcolor: 'rgba(0,0,0,0.6)', 
                    color: 'white', 
                    px: 1, 
                    py: 0.5, 
                    borderRadius: 1,
                    fontSize: '0.75rem'
                  }}>
                    Click to expand
                  </Box>
                </Box>
              )}
              
              {post.video && (
                <Box sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
                  <CardMedia 
                    component="video" 
                    src={post.video} 
                    controls 
                    sx={{ 
                      maxHeight: 350, 
                      objectFit: "cover",
                      width: '100%'
                    }} 
                  />
                </Box>
              )}

              {/* Action buttons */}
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "space-between",
                borderTop: '1px solid #eee',
                pt: 1.5,
                mt: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton 
                    color={liked ? "error" : "default"} 
                    onClick={() => handleLike(post)}
                    size="small"
                    sx={{ mr: 0.5 }}
                  >
                    {liked ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
                  </IconButton>
                  <Typography variant="body2" sx={{ mr: 2 }}>
                    {post.likes && Array.isArray(post.likes) ? post.likes.length : 0}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton 
                    color="primary" 
                    onClick={() => toggleShowAllComments(post._id)}
                    size="small"
                    sx={{ mr: 0.5 }}
                  >
                    <ChatBubbleOutlineIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" sx={{ mr: 2 }}>
                    {post.comments?.length || 0}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton 
                    color="info" 
                    onClick={(e) => handleShareClick(e, post)}
                    size="small"
                    sx={{ mr: 0.5 }}
                  >
                    <ShareIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="body2">
                    Share
                  </Typography>
                </Box>
              </Box>

              {/* Comments section */}
              {renderComments(post)}
              {shareMessage && <Typography color="info.main">{shareMessage}</Typography>}
            </Box>
          </Card>
        );
      })}
      
      {/* Image Modal */}
      <Dialog
        open={imageModalOpen}
        onClose={closeImageModal}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            m: 0,
            maxHeight: '100vh',
            maxWidth: '100vw'
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box sx={{ 
          position: 'relative', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          p: 2,
          maxHeight: '100vh',
          maxWidth: '100vw'
        }}>
          <img 
            src={selectedImage} 
            alt="Expanded view" 
            style={{ 
              maxWidth: 'calc(100vw - 48px)', 
              maxHeight: 'calc(100vh - 48px)', 
              objectFit: 'contain',
              borderRadius: 8
            }} 
          />
          <IconButton
            onClick={closeImageModal}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.7)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Dialog>
      
      {/* Share Menu */}
      <Menu
        anchorEl={shareMenuAnchor}
        open={Boolean(shareMenuAnchor)}
        onClose={handleShareClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
          }
        }}
      >
        <MenuItem onClick={() => handleShareOption('email')}>
          <EmailIcon sx={{ mr: 1, fontSize: '1.1rem' }} /> Email
        </MenuItem>
        <MenuItem onClick={() => handleShareOption('whatsapp')}>
          <WhatsAppIcon sx={{ mr: 1, fontSize: '1.1rem' }} /> WhatsApp
        </MenuItem>
        <MenuItem onClick={() => handleShareOption('facebook')}>
          <FacebookIcon sx={{ mr: 1, fontSize: '1.1rem' }} /> Facebook
        </MenuItem>
        <MenuItem onClick={() => handleShareOption('twitter')}>
          <TwitterIcon sx={{ mr: 1, fontSize: '1.1rem' }} /> Twitter
        </MenuItem>
        <MenuItem onClick={() => handleShareOption('copy')}>
          <ShareIcon sx={{ mr: 1, fontSize: '1.1rem' }} /> Copy Link
        </MenuItem>
      </Menu>
    </Box>
  );

  // Add the delete post function
  const handleDeletePost = async (postId) => {
    if (!userId) return;
    
    // Confirm deletion
    const confirmDelete = window.confirm("Are you sure you want to delete this post?");
    if (!confirmDelete) return;
    
    try {
      const res = await fetch(`https://api.agromanch.com/api/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });
      
      if (res.ok) {
        // Remove the post from the state
        setPosts(prevPosts => prevPosts.filter(post => post._id !== postId));
        setMessage("Post deleted successfully!");
      } else {
        const errorData = await res.json();
        setMessage(`Failed to delete post: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Delete post error:", error);
      setMessage(`Failed to delete post: ${error.message}`);
    }
    
    // Clear message after 3 seconds
    setTimeout(() => setMessage(""), 3000);
  };

  // Updated handleSubmit with progress tracking
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("=== POST SUBMISSION START ===");
    console.log("Content:", content);
    console.log("Media preview:", mediaPreview);
    console.log("Media type:", mediaType);
    console.log("File input:", fileInputRef.current?.files);
    
    if (!content.trim() && !mediaPreview) {
      console.log("No content or media to post");
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    let image = null, video = null;
    
    if (mediaPreview && mediaType && fileInputRef.current?.files[0]) {
      console.log("=== UPLOADING MEDIA TO SERVER ===");
      console.log("File to upload:", fileInputRef.current.files[0]);
      console.log("File type:", fileInputRef.current.files[0].type);
      console.log("File size:", fileInputRef.current.files[0].size);
      
      // Upload to server endpoint
      const formData = new FormData();
      formData.append('media', fileInputRef.current.files[0]);
      
      try {
        console.log("Sending request to server for media upload...");
        console.log("FormData entries:", Array.from(formData.entries()));
        
        // Create XMLHttpRequest for progress tracking
        const xhr = new XMLHttpRequest();
        
        // Track upload progress
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;
            setUploadProgress(percentComplete);
            console.log(`Upload progress: ${percentComplete}%`);
          }
        });
        
        // Convert XMLHttpRequest to Promise
        const uploadPromise = new Promise((resolve, reject) => {
          xhr.open('POST', 'https://api.agromanch.com/api/posts/upload-media', true);
          
          xhr.onload = function() {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const data = JSON.parse(xhr.responseText);
                resolve(data);
              } catch (e) {
                reject(new Error('Invalid JSON response'));
              }
            } else {
              reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
            }
          };
          
          xhr.onerror = function() {
            reject(new Error('Network error'));
          };
          
          xhr.send(formData);
        });
        
        const data = await uploadPromise;
        console.log("Server upload response data:", data);
        
        if (data.mediaUrl) {
          if (data.mediaType === 'image') {
            image = data.mediaUrl;
            console.log("Image uploaded successfully:", image);
          } else if (data.mediaType === 'video') {
            video = data.mediaUrl;
            console.log("Video uploaded successfully:", video);
          }
        } else {
          console.error("No mediaUrl in server response:", data);
          throw new Error("No mediaUrl received from server");
        }
      } catch (error) {
        console.error("Server upload error:", error);
        setMessage(`Failed to upload media: ${error.message}`);
        setUploading(false);
        return;
      }
    } else {
      console.log("No media to upload or file not found");
    }
    
    // Create post
    try {
      console.log("=== CREATING POST ===");
      console.log("Post data:", { userId, content, image, video });
      
      const res = await fetch('https://api.agromanch.com/api/posts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, content, image, video })
      });
      
      console.log("Post creation response status:", res.status);
      
      if (res.ok) {
        const postData = await res.json();
        console.log("Post created successfully:", postData);
        setContent("");
        setMediaPreview(null);
        setMediaType(null);
        setUploadProgress(0);
        setMessage("Post created!");
        fetchPosts();
      } else {
        const errorData = await res.json();
        console.error("Post creation failed:", errorData);
        setMessage(`Failed to create post: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Post creation error:", error);
      setMessage(`Failed to create post: ${error.message}`);
    }
    setUploading(false);
    console.log("=== POST SUBMISSION END ===");
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    console.log("=== FILE SELECTION ===");
    console.log("Selected file:", file);
    
    if (!file) {
      console.log("No file selected");
      return;
    }
    
    console.log("File details:", {
      name: file.name,
      type: file.type,
      size: file.size,
      lastModified: file.lastModified
    });
    
    const reader = new FileReader();
    reader.onloadend = () => {
      console.log("File preview created");
      console.log("Preview type:", file.type.startsWith('image') ? 'image' : 'video');
      setMediaPreview(reader.result);
      setMediaType(file.type.startsWith('image') ? 'image' : 'video');
    };
    reader.readAsDataURL(file);
  };
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await fetch(`https://api.agromanch.com/api/search-users?q=${encodeURIComponent(searchTerm)}`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data);
      } else {
        setSearchResults([]);
      }
    } catch {
      setSearchResults([]);
    }
    setSearchLoading(false);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const theme = createTheme({
    palette: {
      primary: {
        main: '#4CAF50',
        light: '#81C784',
        dark: '#388E3C',
      },
      secondary: {
        main: '#009688',
        light: '#4DB6AC',
        dark: '#00796B',
      },
      background: {
        default: '#f8faf9',
        paper: '#ffffff',
      },
    },
    typography: {
      fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0px 4px 12px rgba(76, 175, 80, 0.2)',
            },
          },
          contained: {
            background: 'linear-gradient(to right, var(--chakra-colors-green-400), var(--chakra-colors-teal-500))',
            color: 'white',
            boxShadow: '0px 2px 8px rgba(76, 175, 80, 0.15)',
            '&:hover': {
              background: 'linear-gradient(to right, var(--chakra-colors-green-500), var(--chakra-colors-teal-600))',
              boxShadow: '0px 4px 15px rgba(76, 175, 80, 0.25)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: '0px 2px 12px rgba(0,0,0,0.08)',
            '&:hover': {
              boxShadow: '0px 4px 20px rgba(76, 175, 80, 0.12)',
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              color: 'var(--chakra-colors-green-500)',
            },
          },
        },
      },
    },
  });

  // Render tab panel content
  const renderTabContent = () => {
    switch (activeTab) {
      case 0: // Posts tab
        return (
          <>
            {renderPostForm()}
            {renderFeed()}
          </>
        );
      case 1: // News tab
        return (
          <AgricultureNews/>
        );
      case 2: // Government Scheme tab
        return (
          <GovernmentSchemes/>
        );
      case 3: // Articles tab
        return (
          <Articles/>
        );
      default:
        return null;
    }
  };

  // Function to open image in modal
  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    setImageModalOpen(true);
  };

  // Function to close image modal
  const closeImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage("");
  };

  // Function to toggle post description expansion
  const toggleDescription = (postId) => {
    setExpandedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  // Function to check if description is long
  const isDescriptionLong = (text) => {
    return text && text.length > 150;
  };

  // Function to render post description with view more/less
  const renderDescription = (post) => {
    const isLong = isDescriptionLong(post.content);
    const isExpanded = expandedPosts[post._id];
    
    if (!isLong) {
      return (
        <Typography 
          sx={{ 
            mb: 2, 
            color: '#333', 
            fontSize: '0.95rem',
            textAlign: 'left',
            wordBreak: 'break-word'
          }}
        >
          {post.content}
        </Typography>
      );
    }
    
    const shouldTruncate = isLong && !isExpanded;
    const displayText = shouldTruncate ? `${post.content.substring(0, 150)}...` : post.content;
    
    return (
      <Box sx={{ mb: 2 }}>
        <Typography 
          sx={{ 
            color: '#333', 
            fontSize: '0.95rem',
            textAlign: 'left',
            wordBreak: 'break-word'
          }}
        >
          {displayText}
        </Typography>
        <Button 
          onClick={() => toggleDescription(post._id)}
          size="small"
          sx={{ 
            color: 'primary.main', 
            fontWeight: 'bold',
            textTransform: 'none',
            p: 0,
            minWidth: 'auto',
            mt: 0.5
          }}
        >
          {isExpanded ? 'Show less' : 'View more'}
          <ExpandMoreIcon 
            sx={{ 
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }} 
          />
        </Button>
      </Box>
    );
  };

  // Function to open post menu
  const handlePostMenuOpen = (event, postId) => {
    setPostMenuAnchor(event.currentTarget);
    setSelectedPostId(postId);
  };

  // Function to close post menu
  const handlePostMenuClose = () => {
    setPostMenuAnchor(null);
    setSelectedPostId(null);
  };

  // Function to handle delete post from menu
  const handleDeletePostFromMenu = () => {
    if (selectedPostId) {
      handleDeletePost(selectedPostId);
    }
    handlePostMenuClose();
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ 
        maxWidth: 600, 
        mx: "auto", 
        p: 2, 
        bgcolor: 'background.default', 
        minHeight: '100vh',
        background: 'linear-gradient(180deg, rgba(76, 175, 80, 0.05) 0%, rgba(0, 150, 136, 0.05) 100%)'
      }}>
        {/* Tab Navigation */}
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 3, 
            borderRadius: '0 0 16px 16px', 
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            position: 'relative',
          }}
        >
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            variant="fullWidth" 
            indicatorColor="primary"
            textColor="primary"
            sx={{ 
              '& .MuiTab-root': { 
                fontSize: '0.85rem',
                fontWeight: 600,
                py: 1.5,
                color: '#666',
                minHeight: '56px'
              },
              '& .Mui-selected': {
                color: 'primary.main',
              },
              '& .MuiTabs-indicator': {
                height: '3px',
                borderRadius: '3px 3px 0 0',
              }
            }}
          >
            <Tab 
              icon={<ArticleIcon />} 
              label={t('posts')} 
              sx={{ 
                color: activeTab === 0 ? 'primary.main' : 'text.secondary',
                '& .MuiSvgIcon-root': { 
                  fontSize: '1.3rem',
                  mb: 0.5
                }
              }} 
            />
            <Tab 
              icon={<CalendarMonthIcon />} 
              label={t('news')} 
              sx={{ 
                color: activeTab === 1 ? 'primary.main' : 'text.secondary',
                '& .MuiSvgIcon-root': { 
                  fontSize: '1.3rem',
                  mb: 0.5
                }
              }}
            />
            <Tab 
              icon={<AssignmentIcon />} 
              label={t('governmentScheme')} 
              sx={{ 
                color: activeTab === 2 ? 'primary.main' : 'text.secondary',
                '& .MuiSvgIcon-root': { 
                  fontSize: '1.3rem',
                  mb: 0.5
                },
                fontSize: '0.75rem'
              }}
            />
            <Tab 
              icon={<LibraryBooksIcon />} 
              label="Articles" 
              sx={{ 
                color: activeTab === 3 ? 'primary.main' : 'text.secondary',
                '& .MuiSvgIcon-root': { 
                  fontSize: '1.3rem',
                  mb: 0.5
                }
              }}
            />
          </Tabs>
        </Paper>

        {activeTab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3, width: '100%' }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, width: '100%' }}>
              <Button
                variant={showAllPosts ? "contained" : "outlined"}
                color="primary"
                onClick={() => setShowAllPosts(true)}
                sx={{ 
                  flex: 1, 
                  py: 1.2,
                  bgcolor: showAllPosts ? 'primary.main' : 'transparent',
                  color: showAllPosts ? 'white' : 'primary.main',
                  borderColor: showAllPosts ? 'primary.main' : 'divider',
                  fontWeight: 'bold',
                  
                  fontSize: '0.8rem',
                  letterSpacing: 0.5,
                  boxShadow: showAllPosts ? 2 : 0,
                  '&:hover': {
                    bgcolor: showAllPosts ? 'primary.dark' : 'rgba(25, 118, 210, 0.08)',
                    boxShadow: showAllPosts ? 3 : 0,
                  },
                }}
              >
                {t('allPosts')}
              </Button>
              <Button
                variant={!showAllPosts ? "contained" : "outlined"}
                color="primary"
                onClick={() => setShowAllPosts(false)}
                sx={{ 
                  flex: 1,
                  py: 1.2,
                  bgcolor: !showAllPosts ? 'primary.main' : 'transparent',
                  color: !showAllPosts ? 'white' : 'primary.main',
                  borderColor: !showAllPosts ? 'primary.main' : 'divider',
                  fontWeight: 'bold',
                  fontSize: '0.8rem',
                  letterSpacing: 0.5,
                  boxShadow: !showAllPosts ? 2 : 0,
                  '&:hover': {
                    bgcolor: !showAllPosts ? 'primary.dark' : 'rgba(25, 118, 210, 0.08)',
                    boxShadow: !showAllPosts ? 3 : 0,
                  },
                }}
              >
                {t('followingPosts')}
              </Button>
            </Box>

            {/* <Box component="form" onSubmit={handleSearch} sx={{ width: '100%' }}>
              <TextField
                size="small"
                placeholder="Search users..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton type="submit" size="small" color="primary">
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 20,
                    bgcolor: '#f8f9fa',
                    height: 40,
                    px: 1,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e0e0e0',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#bdbdbd',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    }
                  }
                }}
                fullWidth
                sx={{ 
                  '& .MuiInputBase-root': {
                    paddingLeft: 2
                  }
                }}
              />
            </Box> */}
            
          </Box>
        )}
        
        {activeTab === 0 && searchLoading && <Box sx={{ textAlign: 'center', mb: 2 }}><CircularProgress size={20} /></Box>}
        {activeTab === 0 && searchResults.length > 0 && (
          <Box sx={{ mb: 2, p: 2, border: '1px solid #eee', borderRadius: 2, background: '#fafafa' }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>User Results:</Typography>
            <List>
              {searchResults.map(user => (
                <ListItem key={user._id} secondaryAction={
                  <Button size="small" variant="contained" href={`/profile/${user.email}`}>View Profile</Button>
                }>
                  <ListItemAvatar><Avatar src={user.picture} /></ListItemAvatar>
                  <ListItemText primary={user.name} secondary={user.email} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        
        {renderTabContent()}
      </Box>
    </ThemeProvider>
  );
};

export default SocialMedia;